import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';

const UpgradePrompt = ({ 
  open, 
  onClose, 
  feature, 
  requiredPlan = 'Basic',
  currentPlan = 'Free'
}) => {
  const navigate = useNavigate();

  const featureDetails = {
    'social_integration': {
      title: 'Social Media Integration',
      description: 'Connect and manage your social media accounts directly from the platform',
      benefits: [
        'Schedule posts across multiple platforms',
        'Track engagement metrics',
        'Auto-post survey results',
        'Social media analytics'
      ]
    },
    'ai_assistant': {
      title: 'AI Post Assistant',
      description: 'Generate engaging content using AI',
      benefits: [
        'AI-powered post generation',
        'Multiple content variations',
        'Tone and style customization',
        'Time-saving automation'
      ]
    },
    'ai_analysis': {
      title: 'AI Survey Analysis',
      description: 'Get AI-powered insights from your survey responses',
      benefits: [
        'Sentiment analysis',
        'Theme extraction',
        'Automated summaries',
        'Feature recommendations'
      ]
    },
    'jira_integration': {
      title: 'Atlassian Integration',
      description: 'Connect Jira, Jira Product Discovery, and Confluence to your workflow',
      benefits: [
        'Push features as Jira Epics and requirements as Stories',
        'Push surveys and features to JPD as Ideas for prioritization',
        'Publish design documents to Confluence or as Jira attachments',
        'Read back JPD prioritization data into your platform',
        'Automatic change detection — only push when content changes',
      ]
    },
    'advanced_analytics': {
      title: 'Advanced Analytics',
      description: 'Access detailed analytics and reports',
      benefits: [
        'Custom reports',
        'Export to multiple formats',
        'Trend analysis',
        'Predictive insights'
      ]
    },
    'api_access': {
      title: 'API Access',
      description: 'Access our full API for custom integrations',
      benefits: [
        'RESTful API',
        'Webhook support',
        'Custom integrations',
        'Developer documentation'
      ]
    }
  };

  const details = featureDetails[feature] || {
    title: 'Premium Feature',
    description: 'This feature requires a paid plan',
    benefits: []
  };

  const handleUpgrade = () => {
    navigate('/pricing');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="warning" />
          <Typography variant="h6">
            Upgrade Required
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          You're currently on the <strong>{currentPlan}</strong> plan.
          Upgrade to <strong>{requiredPlan}</strong> or higher to unlock this feature.
        </Alert>

        <Typography variant="h6" gutterBottom>
          {details.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {details.description}
        </Typography>

        {details.benefits.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              What you'll get:
            </Typography>
            <List dense>
              {details.benefits.map((benefit, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <StarIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>
          Maybe Later
        </Button>
        <Button 
          variant="contained" 
          onClick={handleUpgrade}
          startIcon={<StarIcon />}
        >
          Upgrade Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePrompt;
